/* -----------------------------------
Copyright: Logical Developments 2025.
Project:   ConNote Portal
Filename:  PageReview.js
Author:    Jordan R. Bock
Version:   0.06
Description:
The Review Drop Freight page for the Drop Freight workflow.

History:
0.06  01-04-26 MJB  LD0013974 Updated summary display to match the vendor portal style
0.05  02-04-25 JRB  LD0013312 Do not handle data update here. Do it in the download step after confirming the email.
0.04  17-07-24 JRB (ld0012843) Added Print Label Button when a consignment is linked.
0.03  05-06-24 JRB (ld0012770) Added Quote Number to displayed fields. Fixed Edit button destinations
0.02  08-03-24 JRB (ld0012663) Do not set deliveryRequired here. It is handled in pageServices.
0.01	24-01-24 JRB   Created.
----------------------------------- */

import React, { useEffect, useState } from 'react';
import CardForm from '../Common/CardForm';
import { legal } from '../Configuration/Config';
import useNavigate from '../Navigation/useNavigate';
import useAuthenticate from '../Session/useAuthenticate';
// import { displayPkpMsg } from '../Common/utils';
import { FreightSummary } from '../Common/FreightCards'
// import { compactStore } from '../Common/utils';
// import useRest from '../Session/useRest';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import '../Common/PageReview.css'

class init {
  constructor () {
    // this.deliveryRequired = {value: 1, error: null};
    this.scheduleDay = {value: null, error: null}; // Date object
  }
}

// Receives the store property from the NewQuote store and the action
function reducer(store, action) {
  let newValue = action.value;
  switch (action.name) {
    case 'scheduleDay':
      return {
        scheduleDay:  {value: newValue ? new Date(newValue) : null, error: null}
      }

    case 'dateBooked':
      if (newValue) {
        const bookedDate = new Date(newValue)
        return {
          bookedAt: {
            value: bookedDate.toISOString(),
            error: null
          }
        }
      } else {
        return {
          bookedAt: {
            value: null,
            error: null
          }
        }
      }

    case 'loadStore':
      // if (newValue.pickupRequired < 0 || newValue.pickupRequired > 3) newValue.pickupRequired = 0;
      if (newValue.deliveryRequired < 0 || newValue.deliveryRequired > 3) newValue.deliveryRequired = 0;
      return {
        ...reducer(store, {name: 'scheduleDay', value: newValue.scheduleDay}),
        ...reducer(store, {name: 'dateBooked', value: newValue.dateBooked}),
        deliveryRequired: {value: newValue.deliveryRequired, error: null}

        // bookedAt: {value: newValue.dateBooked, error: null}
      }

    default:
      throw new Error(`Services Reducer (${action.name}) is not supported`);
  };
}

function PageReview({store, controls}) {
  const { loggedIn, customer } = useAuthenticate();
  const { navState, changeState } = useNavigate();
  const [ error, setError ] = useState(null);
  const navigate = useRouterNavigate();

  // There is no interaction on the page so do not need useEffects, re-rendering everything is okay.

  const nextCall = (enabled) => {
  //   // time may have lapsed, validate the scheduled time
  //   let today = new Date().setHours(0,0,0,0)
  //   let booked = new Date(store.readyAt.value).setHours(0,0,0,0)
  //   if ( booked < today)
  //     setError('Scheduled drop in date cannot be in the past');
  //   else if (enabled)
  //     bookConnote(compactStore(store));
   controls.next.call(enabled);
  }

  const doLabel = (enabled) => {
    if (enabled) {
      navigate('/consignments/generatelabel', {state: {conNumber: store.conNumber.value}})
    }
  }

  // useEffect(() => {
  //   if (connoteData || connoteError) {
  //     if (connoteData.success === true)
  //       controls.next.call(true);
  //     else if (connoteData.success === false)
  //       setError(connoteData.message)
  //     else if (connoteError)
  //       setError(connoteError)
  //   }
  // },[connoteData, connoteError])

  // go directly to the page number passed
  const gotoDetails = (pageNum) => {
    const newMenu = navState.stepArray.map((item, index) => ({ ...item, click: index + 1 <= pageNum })); 
    changeState({ ...navState, stepCurrent: pageNum, stepArray: newMenu});
  }

  const sender = store.sender;
  const receiver = store.receiver;

  const senderAddrLine1 = sender.streetAddress.value.trim();
  const senderAddrLine2 = `${sender.suburb.value}, ${sender.state.value.trim()} ${sender.postCode.value.trim()}`.trim();

  const receiverAddrLine1 = receiver.streetAddress.value.trim();
  const receiverAddrLine2 = `${receiver.suburb.value}, ${receiver.state.value.trim()} ${receiver.postCode.value.trim()}`.trim();
  
  const canEdit = store.status.value === 2;
  // const pickupMessage = displayPkpMsg(store.readyAt.value, store.closedAfter.value, null, store.bookedAt.value ? store.bookedAt.value : undefined); // Use undefined so we don't pass anything in if not booked yet.
 
  return (
    <CardForm
      title='Review Your Drop In'
      controls={canEdit
        ? { ...controls,
            next: {
              ...controls.next,
              name: 'Book Drop In',
              call: nextCall,
              enable: error === null
            }
          }
        : {
          next: {
            ...controls.next,
            name: 'Print Label',
            call: doLabel,
            enable: true
          }}
      }
      className="workflow"
      error={error}
    >
      {/* Overall grid */}
      <div className='page-review-grid'>
        {/* Left column, sender and receiver addresses */}
        <div className='page-review-column'>
          {/* Edit button */}
          {canEdit && <div style={{alignSelf: "flex-end"}}>
            <button className='btn__link' onClick={() => gotoDetails(3)}>Edit</button>
          </div>}

          {/* Sender */}
          <div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Sender</h3>
            <div className='page-review-cell-content'>{store.sender.company.value}</div>
            <div className='page-review-cell-content'>{senderAddrLine1}</div>
            <div className='page-review-cell-content'>{senderAddrLine2}</div>
          </div>
          
          {/* Receiver */}
          <div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Receiver</h3>
            <div className='page-review-cell-content'>{store.receiver.company.value}</div>
            <div className='page-review-cell-content'>{receiverAddrLine1}</div>
            <div className='page-review-cell-content'>{receiverAddrLine2}</div>
          </div>
        </div>

        {/* Right column, consignment/account information */}
        <div className='page-review-column'>
          {/* Edit button */}
          {canEdit && <div style={{alignSelf: "flex-end"}}>
            <button className='btn__link' onClick={() => gotoDetails(3)}>Edit</button>
          </div>}

          {/* Account details */}
          <div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Account</h3>
            <div className='page-review-cell-content'>{loggedIn && customer.company}</div>
          </div>
          
          {/* Consignment number */}
          {store.conNumber.value !== '' && (<div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Consignment Number</h3>
            <div className='page-review-cell-content'>{store.conNumber.value}</div>
          </div>)}

          {/* Quote number */}
          {store.quoteNumber.value !== '' && (<div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Quote Number</h3>
            <div className='page-review-cell-content'>{store.quoteNumber.value}</div>
          </div>)}

          {/* End of trip information */}
          <div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Additional services</h3>
            {store.deliveryRequired.value === 1 && (<div className='page-review-cell-content'>Collected from depot</div>)}
            {store.deliveryRequired.value >= 2 && (<div className='page-review-cell-content'>Delivery Required</div>)}
            {store.deliveryRequired.value === 3 && (<div className='page-review-cell-content'>Forklift Required</div>)}
          </div>

          {/* Scheduled Drop In Date */}
          {store._readyAtDate.value !== null && (<div className='page-review-cell'>
            <h3 className='page-review-cell-title'>Scheduled Drop In Date</h3>
            <div className='page-review-cell-content'>{store._readyAtDate.value}</div>
          </div>)}

        </div>
      </div>
      
      <FreightSummary store={store.freightList} title={<h3>Freight Details</h3>} edit={canEdit ? () => gotoDetails(4) : null} />      {store.instructions.value ? <h3 className='accent'>Special Instructions</h3> : ''}
      <p>{store.instructions.value}</p>
      <section className='accent stack'>
        {legal.body}{store.status.value !== 2 ? legal.consignmentComplete : '' }
      </section>
    </CardForm>
  )
}

export default PageReview;
export { init, reducer };